import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { TextField } from "@rentiohq/web-shared/dist/components";
import React from "react";
import {
  formatNumberInput,
  numberInputErrorText,
  parseLocaleNumber,
} from "../numberInput.utils";

interface ILocaleNumberFieldProps {
  name: string;
  label?: string;
  labelHidden?: boolean;
  // The number the form holds; undefined while the field is empty or wrong.
  value?: number;
  // Called on every keystroke with the number that was read, or undefined
  // when the text is empty or not (yet) a valid number.
  onChange: (value?: number) => void;
  maxDecimals?: number;
  max?: number;
  // Money keeps two decimals when the field is left ("1.250,00").
  isMoney?: boolean;
  // What a minus sign means here; a money field explains that credit notes
  // are not supported.
  negativeMessage?: string;
  suffix?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
}

const toText = (params: { value?: number; isMoney: boolean }): string =>
  params.value == null
    ? ""
    : formatNumberInput(params.value, { isMoney: params.isMoney });

// An empty field and 0 are the same value for the form.
const isSameNumber = (left?: number, right?: number) =>
  (left ?? 0) === (right ?? 0);

// Free-text number field with Belgian notation ("1.234,56"). The typed text is
// local on purpose: echoing a re-formatted number back while typing erases a
// half-typed decimal ("1," would come back as "1"). The field shows what it
// understood once the user leaves it.
export const LocaleNumberField = (props: ILocaleNumberFieldProps) => {
  const {
    name,
    label,
    labelHidden,
    value,
    onChange,
    maxDecimals,
    max,
    isMoney = false,
    negativeMessage,
    suffix,
    placeholder,
    error,
    disabled,
  } = props;

  // State
  const [text, setText] = React.useState(() => toText({ value, isMoney }));
  const isFocused = React.useRef(false);

  const read = (raw: string) => {
    const result = parseLocaleNumber(raw, { maxDecimals });
    const isTooHigh =
      !result.error &&
      result.value != null &&
      max != null &&
      result.value > max;
    return {
      ...result,
      isTooHigh,
      accepted: result.error || isTooHigh ? undefined : result.value,
    };
  };

  // Lifecycle
  // INFO: follow a value that changed outside this field (a derived amount, a
  // reset), but never while the user is typing in it.
  React.useEffect(() => {
    if (isFocused.current) return;
    if (!isSameNumber(read(text).accepted, value)) {
      setText(toText({ value, isMoney }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Event handlers
  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const next = event.target.value;
    setText(next);
    onChange(read(next).accepted);
  };

  const handleFocus = () => {
    isFocused.current = true;
  };

  const handleBlur = () => {
    isFocused.current = false;
    const { accepted } = read(text);
    if (accepted == null) return;
    // The form may have adjusted the number (a tenant amount rounds to the
    // nearest two-decimal share): show the value that counts.
    setText(toText({ value: value ?? accepted, isMoney }));
  };

  // Render
  const ownError = (() => {
    const { error: parseError, isTooHigh } = read(text);
    if (isTooHigh && max != null) {
      return getLocalizedText("costs.input.too_high", {
        max: formatNumberInput(max),
      });
    }
    return numberInputErrorText({
      error: parseError,
      maxDecimals,
      negativeMessage,
    });
  })();

  return (
    <TextField
      name={name}
      label={label ?? "_"}
      labelHidden={labelHidden ?? label == null}
      type="text"
      placeholder={placeholder}
      suffix={suffix}
      value={text}
      error={ownError ?? error}
      disabled={disabled}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
};
